<script lang="ts" generics="S, O">
  import { onDestroy } from 'svelte';
  import CodeEditor from '../CodeEditor.svelte';
  import OutputPanel from '../OutputPanel.svelte';
  import VerdictBadge from '../VerdictBadge.svelte';
  import { SUBMIT_CHORD_LABEL } from '../keyboard';
  import { sandbox } from '../../services';
  import { withVizHelper } from '../../lib/viz';
  import {
    byteLength,
    isAbortError,
    retryNotice,
    SandboxError,
    SANDBOX_ERROR_MESSAGES,
  } from '../../lib/sandboxd';
  import { recordRunOutcome, withSpan, withSpanUnder } from '../../lib/telemetry';
  import {
    failureOutcome,
    judgeReplay,
    type LabId,
    type ReplayPlan,
    type ReplayResult,
  } from '../../lib/labs';
  import FrameBoard from './FrameBoard.svelte';
  import FramePlayer from './FramePlayer.svelte';
  import type { LabSession } from './lab-session.svelte';

  /**
   * 段階 3「コードで作る」（labs.md 手順 5「再現」、components.md §3.2 段階 3）。
   * 記録から作ったコードを sandboxd で実行し、`@@viz` のフレームを記録の盤面と 1 ステップずつ比べる。
   */
  interface Props {
    labId: LabId;
    session: LabSession<S, O>;
    /** 盤面に描く変数。 */
    labels: string[];
    /** 記録が空のとき、例の操作を入れる。 */
    onexample?: (() => void) | undefined;
  }

  let { labId, session, labels, onexample }: Props = $props();

  const plan = $derived(session.plan);
  const code = $derived(session.editorCode ?? plan.code);
  let running = $state(false);
  let notice = $state('');
  let failure = $state('');
  let result = $state.raw<ReplayResult | null>(null);
  /** 実行した時点の期待フレーム（実行後に記録が変わっても、比べた相手を保つ）。 */
  let checkedPlan = $state.raw<ReplayPlan | null>(null);
  const checked = $derived(checkedPlan ?? plan);
  let step = $state(0);
  /** 実行中のリクエストの中止用。画面を離れたら中止し、次の実行が local-busy にならないようにする。 */
  let controller: AbortController | null = null;
  onDestroy(() => controller?.abort());

  const count = $derived(Math.max(checked.frames.length, result?.frames.length ?? 0));
  const marks = $derived(
    result && result.verdict !== 'CE'
      ? Array.from({ length: count }, (_, i) => (result?.matches[i] ? '✓' : '✕'))
      : [],
  );
  const errorLines = $derived(
    result?.verdict === 'CE' && result.errorLine !== null
      ? [{ line: result.errorLine, message: result.compileOutput.split('\n')[0] ?? '' }]
      : [],
  );
  const codeLines = $derived(code.split('\n'));

  function onchange(v: string) {
    session.editorCode = v;
  }

  function resetCode() {
    session.editorCode = null;
    result = null;
    checkedPlan = null;
    failure = '';
  }

  async function run() {
    if (running) return;
    running = true;
    failure = '';
    notice = '';
    const ctrl = new AbortController();
    controller = ctrl;
    const attached = withVizHelper(code);
    const req = { code: attached.code, stdins: [''] };
    const expected = plan;
    try {
      await withSpan(
        'class.lab.check',
        { 'class.lab.id': labId, 'class.lab.action': 'check' },
        (checkSpan) =>
          withSpanUnder(
            checkSpan,
            'class.run',
            {
              'class.run.purpose': 'lab',
              'class.run.cases': req.stdins.length,
              'class.run.code_bytes': byteLength(req.code),
              'class.lab.id': labId,
              'class.lab.action': 'run',
            },
            async (span) => {
              try {
                const res = await sandbox.run(req, {
                  signal: ctrl.signal,
                  onRetry: (attempt) => (notice = retryNotice(attempt)),
                });
                const r = judgeReplay(res, attached.userLineCount, expected.frames);
                recordRunOutcome(span, r.outcome, r.verdict);
                checkSpan.setAttribute('class.verdict', r.verdict);
                checkedPlan = expected;
                result = r;
                step = r.firstMismatch ?? 0;
              } catch (e) {
                if (isAbortError(e)) return;
                if (e instanceof SandboxError) {
                  recordRunOutcome(span, failureOutcome(e.kind));
                  failure = e.message;
                } else {
                  recordRunOutcome(span, 'http_error');
                  failure = SANDBOX_ERROR_MESSAGES.protocol;
                }
              }
            },
          ),
      );
    } finally {
      running = false;
      notice = '';
      if (controller === ctrl) controller = null;
    }
  }
</script>

<div class="lab replay" data-testid="lab-replay">
  <section class="panel col" aria-labelledby="replay-board-title">
    <h2 id="replay-board-title">盤面</h2>
    {#if result === null}
      <p class="text-soft small">
        記録の盤面（{checked.frames.length} ステップ）です。実行すると、コードから届いた盤面と並べます。
      </p>
      <FramePlayer count={plan.frames.length} bind:step label="記録の盤面のステップ操作">
        {#snippet children(i)}
          {@const f = plan.frames[i]}
          {#if f}<FrameBoard state={f.state} {labels} />{/if}
          <p class="src small">
            <span class="text-soft">この盤面を作った行:</span>
            <code>{plan.code.split('\n')[(plan.frameLines[i] ?? 1) - 1] ?? ''}</code>
          </p>
        {/snippet}
      </FramePlayer>
    {:else}
      <div class="verdict-line" role="status">
        <VerdictBadge verdict={result.verdict} />
        <span>{result.message}</span>
      </div>
      {#if result.verdict !== 'CE'}
        <FramePlayer {count} bind:step {marks} label="盤面のステップ操作">
          {#snippet children(i)}
            {@const want = checked.frames[i]}
            {@const got = result?.frames[i]}
            <div class="pair">
              <div>
                <h3 class="small">記録の盤面</h3>
                {#if want}<FrameBoard state={want.state} {labels} />{:else}<FrameBoard
                    state={{}}
                    missing
                  />{/if}
              </div>
              <div>
                <h3 class="small">
                  コードから届いた盤面
                  {#if result?.matches[i]}<span class="ok">✓ 一致</span>{:else}<span class="ng"
                      >✕ 不一致</span
                    >{/if}
                </h3>
                {#if got}<FrameBoard state={got.state} {labels} />{:else}<FrameBoard
                    state={{}}
                    missing
                  />{/if}
              </div>
            </div>
          {/snippet}
        </FramePlayer>
      {/if}
    {/if}
  </section>

  <section class="panel col" aria-labelledby="replay-code-title">
    <h2 id="replay-code-title">コード</h2>
    {#if session.run.steps.length === 0 && session.editorCode === null}
      <p class="small">
        段階 1 で操作すると、その記録がここに届きます。
        {#if onexample}
          <button type="button" class="btn btn-sm" onclick={onexample}>例の操作を入れる</button>
        {/if}
      </p>
    {/if}
    <CodeEditor
      value={code}
      {onchange}
      onsubmit={run}
      minLines={Math.min(Math.max(codeLines.length, 8), 24)}
      {errorLines}
      label="再現する Swift のコード"
    />
    <div class="actions">
      <button
        type="button"
        class="btn btn-primary"
        onclick={run}
        disabled={running}
        aria-busy={running ? 'true' : undefined}
        data-testid="lab-run"
      >
        {running ? '実行中' : 'コードで確かめる'} <kbd>{SUBMIT_CHORD_LABEL}</kbd>
      </button>
      <button type="button" class="btn btn-quiet" onclick={resetCode} disabled={running}
        >記録から作り直す</button
      >
      <span class="small" role="status">{notice}</span>
    </div>
    {#if failure}
      <p class="failure" role="alert">{failure}もう一度送れます。</p>
    {/if}
    {#if result?.verdict === 'CE'}
      <pre class="code compile" tabindex="-1">{result.compileOutput}</pre>
    {:else if result}
      {#if checked.stdout !== '' || result.stdout !== ''}
        <OutputPanel mode="diff" expected={checked.stdout} actual={result.stdout} />
      {:else if result.stderr !== ''}
        <OutputPanel mode="plain" stderr={result.stderr} />
      {/if}
    {/if}
  </section>
</div>

<style>
  .lab {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: var(--space-5);
    align-items: start;
  }
  .col {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    min-width: 0;
  }
  .col h2 {
    margin: 0;
  }
  .small {
    font-size: var(--fs-sm);
  }
  h3.small {
    margin: 0 0 var(--space-2);
    display: flex;
    gap: var(--space-2);
    align-items: baseline;
  }
  .pair {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: var(--space-3);
  }
  .ok {
    color: var(--ok-text);
  }
  .ng {
    color: var(--err-text);
  }
  .verdict-line {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
    font-weight: var(--fw-semibold);
  }
  .src code {
    font-family: var(--font-mono);
  }
  .actions {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
  }
  .failure {
    color: var(--err-text);
    background: var(--err-bg);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    font-size: var(--fs-sm);
  }
  .compile {
    white-space: pre-wrap;
    font-size: var(--fs-xs);
    max-height: 16rem;
    overflow: auto;
  }
  @media (max-width: 1023px) {
    .lab {
      grid-template-columns: minmax(0, 1fr);
    }
  }
  @media (max-width: 639px) {
    .pair {
      grid-template-columns: minmax(0, 1fr);
    }
  }
</style>
